// Pieces of /pricing (layout A, owner-approved 2026-10-11): split hero with a
// price anchor, plan cards with one row of key numbers, guarantee strip, the
// list of check locations, and the comparison-table cells. The Free card
// (OpenSpec 123) comes first; the paid cards follow.
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Check, MapPin, Minus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { MSection, SectionHeader } from '@/components/marketing/section'
import { FREE_DOMAIN_BULLET } from '@/lib/free-domain'
import { FREE_PLAN, type FreePlanState } from '@/lib/free-plan'
import { CONTINENTS, LOCATIONS } from '@/lib/locations'
import { MAX_ANNUAL_SAVING_PERCENT, tiers, type PaidPlan } from '@/lib/plans'
import { cn } from '@/lib/utils'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

type Tier = (typeof tiers)[number]

/** Integer prices bare ($3), fractional ones with 2 decimals ($2.50). */
export const formatPrice = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(2))

export function BillingSwitch({ annual, onChange }: { annual: boolean; onChange: (annual: boolean) => void }) {
  const option = (value: boolean, label: ReactNode) => (
    <button
      type="button"
      role="radio"
      aria-checked={annual === value}
      onClick={() => onChange(value)}
      className={cn(
        'rounded-sm px-4 py-2 text-sm font-medium transition-colors',
        annual === value ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  )
  return (
    <div role="radiogroup" aria-label="Billing period" className="inline-flex rounded-sm border border-border bg-card p-1">
      {option(false, 'Monthly')}
      {option(
        true,
        <>
          Annual <span className="ml-1 text-status-up">save up to {MAX_ANNUAL_SAVING_PERCENT}%</span>
        </>,
      )}
    </div>
  )
}

/**
 * The first paid plan (the one next to Free) at four other products, billed monthly, read
 * from each pricing page in a browser on 2026-10-18. Re-check when a competitor changes
 * plans, and update the "first paid plans" line in CLAUDE.md with it. Sorted by price.
 * UptimeRobot and Hyperping show dollars or euros by where you visit from; the number is
 * the same. `logo` files are each company's own mark in `public/competitors/`.
 */
type Rival = {
  name: string
  plan: string
  monitors: string
  interval: string
  monthly: number
  href: string
  logo: { src: string; kind: 'filled' | 'mark' | 'crop' }
}

const RIVALS: readonly Rival[] = [
  { name: 'Pulsetic', plan: 'Solo', monitors: '10+', interval: '1 min', monthly: 9, href: '/compare/pulsetic', logo: { src: '/competitors/pulsetic.svg', kind: 'filled' } },
  { name: 'HetrixTools', plan: 'Professional', monitors: '30', interval: '1 min', monthly: 9.95, href: '/compare/hetrixtools', logo: { src: '/competitors/hetrixtools.png', kind: 'crop' } },
  { name: 'UptimeRobot', plan: 'Solo', monitors: '10', interval: '1 min', monthly: 10, href: '/compare/uptimerobot', logo: { src: '/competitors/uptimerobot.png', kind: 'mark' } },
  { name: 'Hyperping', plan: 'Essentials', monitors: '50', interval: '30 s', monthly: 29, href: '/compare/hyperping', logo: { src: '/competitors/hyperping.svg', kind: 'mark' } },
]

/** How many times Pro's price a plan costs: 3×, 3.3×, 9.7×. One decimal, dropped when it is .0. */
export const timesPrice = (price: number, base: number) => {
  const x = Math.round((price / base) * 10) / 10
  return `${Number.isInteger(x) ? x : x.toFixed(1)}×`
}

// The table columns on md and up. On a phone the two fact columns fold into one line.
const COLS = 'md:grid md:grid-cols-[minmax(0,1fr)_76px_96px_minmax(0,2fr)] md:items-center md:gap-x-6'

function Logo({ src, kind }: Rival['logo']) {
  return (
    <span
      className={cn(
        'flex size-[38px] shrink-0 items-center justify-center overflow-hidden rounded-lg',
        kind !== 'filled' && 'border border-border bg-background',
      )}
    >
      <img
        src={src}
        alt=""
        width={kind === 'filled' ? 38 : 24}
        height={kind === 'filled' ? 38 : 24}
        decoding="async"
        className={cn(kind === 'filled' ? 'size-full' : 'size-6', kind === 'mark' && 'object-contain', kind === 'crop' && 'object-cover object-left')}
      />
    </span>
  )
}

export function PriceAnchor() {
  const pro = tiers.find((t) => t.plan === 'pro')!
  const rows = [
    { name: 'Uptrack', plan: pro.name, monitors: CARD_FACTS.pro.monitors, interval: '30 s', monthly: pro.monthlyPrice, href: null, logo: { src: '/favicon.svg', kind: 'filled' as const } },
    ...RIVALS.map((r) => ({ ...r })),
  ]
  const max = Math.max(...rows.map((r) => r.monthly))

  return (
    <div>
      <p className="mb-3.5 text-sm text-muted-foreground">The first paid plan at each company, price per month, billed monthly</p>
      <div role="table" aria-label="First paid plan at each company" className="overflow-hidden rounded-lg border border-border bg-card">
        <div
          role="row"
          className={cn('hidden h-12 whitespace-nowrap border-b border-border px-7 text-xs font-medium uppercase tracking-wider text-muted-foreground', COLS)}
        >
          <span role="columnheader">Plan</span>
          <span role="columnheader" className="text-right">Monitors</span>
          <span role="columnheader" className="text-right">Check every</span>
          <span role="columnheader">Per month</span>
        </div>
        {rows.map((r, i) => {
          const us = r.href === null
          return (
            <div
              key={r.name}
              role="row"
              className={cn(
                'relative grid gap-y-2.5 px-[18px] py-4 md:min-h-[76px] md:px-7 md:py-0',
                COLS,
                us
                  ? 'bg-muted md:min-h-[92px] before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-primary'
                  : i > 1 && 'after:absolute after:inset-x-[18px] after:top-0 after:h-px after:bg-border md:after:inset-x-7',
              )}
            >
              <span role="cell" className="flex min-w-0 items-center gap-3.5">
                <Logo {...r.logo} />
                <span className="block leading-tight">
                  <span className="block text-[1.0625rem] font-semibold text-foreground">
                    {r.href ? (
                      <Link to={r.href} className="hover:underline">
                        {r.name}
                      </Link>
                    ) : (
                      r.name
                    )}
                  </span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">{r.plan}</span>
                </span>
              </span>
              <span role="cell" className={cn('num hidden text-right text-base md:block', us ? 'font-medium text-foreground' : 'text-body')}>
                {r.monitors}
              </span>
              <span role="cell" className={cn('hidden text-right text-base md:block', us ? 'font-medium text-foreground' : 'text-body')}>
                {r.interval}
              </span>
              <span className={cn('num text-sm md:hidden', us ? 'text-body' : 'text-muted-foreground')}>
                {r.monitors} monitors · every {r.interval}
              </span>
              <span role="cell" className="grid grid-cols-[1fr_84px] items-center gap-x-5">
                <span className={cn('h-2 overflow-hidden rounded-sm', us ? 'bg-card' : 'bg-muted')}>
                  <span
                    className={cn('price-bar block h-full rounded-sm', us ? 'bg-primary' : 'bg-muted-foreground/40')}
                    style={{ width: `${(r.monthly / max) * 100}%`, animationDelay: `${i * 70 + 150}ms` }}
                  />
                </span>
                <span className="block text-right leading-tight">
                  <span className={cn('num block', us ? 'text-[2rem] font-semibold text-foreground' : 'text-[1.375rem] font-medium text-body')}>
                    ${formatPrice(r.monthly)}
                  </span>
                  <span className={cn('num mt-1 block text-sm', us ? 'text-body' : 'text-muted-foreground')}>
                    {us ? 'our price' : `${timesPrice(r.monthly, pro.monthlyPrice)} Pro`}
                  </span>
                </span>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type CardFacts = { monitors: string; locations: string; locationLine: string; bullets: string[] }

/** The few facts each card leads with. The full list is in the comparison table. */
export const CARD_FACTS: Record<PaidPlan, CardFacts> = {
  pro: {
    monitors: '50',
    locations: MONITOR_LOCATIONS.plan.cardValue.pro,
    locationLine: MONITOR_LOCATIONS.plan.cardLine.pro,
    bullets: ['Email, Slack, Discord, Telegram, webhook alerts', '5 status pages', 'HTTP and keyword checks every 30 seconds', '3 team members · 2 years history'],
  },
  team: {
    monitors: '200',
    locations: MONITOR_LOCATIONS.plan.cardValue.team,
    locationLine: MONITOR_LOCATIONS.plan.cardLine.team,
    bullets: ['Unlimited status pages + custom domain', 'Escalation policies', 'HTTP and keyword checks every 30 seconds', '5 team members · weekly reports'],
  },
  business: {
    monitors: '1,000',
    locations: MONITOR_LOCATIONS.plan.cardValue.business,
    locationLine: MONITOR_LOCATIONS.plan.cardLine.business,
    bullets: ['SSO / SAML', 'Whitelabel + custom email sender', 'HTTP and keyword checks every 30 seconds', '15 team members · 5 years history'],
  },
}

type Metric = { value: string; label: string }

/**
 * One row of key numbers. With four cards side by side (`stacked`) a card is too
 * narrow for three columns on a laptop, so from `lg` it reads as label and value rows.
 */
function Metrics({ items, stacked }: { items: Metric[]; stacked: boolean }) {
  return (
    <div className={cn('my-6 grid grid-cols-3 border-y border-border', stacked && 'lg:grid-cols-1')}>
      {items.map((m, i) => (
        <div
          key={m.label}
          className={cn(
            'py-3.5',
            i > 0 && 'border-l border-border pl-4',
            stacked && 'lg:flex lg:flex-row-reverse lg:items-baseline lg:justify-between lg:py-2.5 lg:pl-0',
            stacked && i > 0 && 'lg:border-l-0 lg:border-t',
          )}
        >
          <div className="num text-[1.375rem] font-semibold text-foreground">{m.value}</div>
          <div className="text-sm text-muted-foreground">{m.label}</div>
        </div>
      ))}
    </div>
  )
}

function FactList({ locationLine, bullets }: { locationLine: ReactNode; bullets: string[] }) {
  return (
    <ul className="flex-1 space-y-3 text-[15px] text-body">
      <li className="flex items-start gap-2.5">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span>
          {locationLine}{' '}
          <a href="#locations" className="whitespace-nowrap text-primary hover:underline">
            See where
          </a>
        </span>
      </li>
      {bullets.map((b) => (
        <li key={b} className="flex items-start gap-2.5">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  )
}

export function PlanCard({
  tier,
  annual,
  popular,
  action,
  stacked = false,
}: {
  tier: Tier
  annual: boolean
  popular: boolean
  action: ReactNode
  /** Four cards in a row (the Free card is shown). */
  stacked?: boolean
}) {
  const facts = CARD_FACTS[tier.plan]
  const metrics = [
    { value: facts.monitors, label: 'monitors' },
    { value: '30 s', label: 'every check' },
    { value: facts.locations, label: 'locations' },
  ]
  return (
    <div
      id={`plan-${tier.plan}`}
      className={cn('flex scroll-mt-24 flex-col rounded-lg border border-border', stacked ? 'p-6' : 'p-7', popular ? 'bg-muted shadow-raised' : 'bg-card')}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">{tier.name}</h2>
        {popular && <Badge variant="secondary">Most popular</Badge>}
      </div>
      <p className={cn('mt-1 text-sm text-body', stacked && 'lg:min-h-10')}>{tier.description}</p>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="num text-5xl font-semibold tracking-tight">${formatPrice(annual ? tier.annualMonthlyPrice : tier.monthlyPrice)}</span>
        <span className="text-muted-foreground">/mo</span>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {annual ? (
          <>
            <span className="num line-through">${tier.monthlyPrice}</span> · billed <span className="num">${tier.annualTotalPrice}</span> yearly
          </>
        ) : (
          'billed monthly'
        )}
      </p>

      <Metrics items={metrics} stacked={stacked} />
      <FactList locationLine={facts.locationLine} bullets={facts.bullets} />

      <div className="mt-7">{action}</div>
    </div>
  )
}

/** The facts the Free card leads with. The numbers come from the public state, the rest are fixed plan terms. */
export type FreeCardFacts = { monitors: number; intervalSeconds: number; locationLabel: string }

/**
 * The Free card (first, left). `state` decides what sits under the button: the
 * Pro offer when the plan is full. The page hides the card when the plan is closed.
 */
export function FreePlanCard({
  facts,
  state,
  action,
  stacked = false,
}: {
  facts: FreeCardFacts
  state: FreePlanState
  action: ReactNode
  stacked?: boolean
}) {
  const metrics = [
    { value: String(facts.monitors), label: 'monitors' },
    { value: `${facts.intervalSeconds} s`, label: 'every check' },
    { value: '1', label: 'location' },
  ]
  const bullets = [
    `HTTP and keyword checks every ${facts.intervalSeconds} seconds`,
    'Email and one chat channel: Discord, Slack or Telegram',
    `${FREE_PLAN.statusPages} status page · ${FREE_PLAN.teamMembers} team member`,
    `${FREE_PLAN.historyDays} days of history`,
    'Commercial use allowed',
    FREE_DOMAIN_BULLET,
  ]
  return (
    <div id="plan-free" className={cn('flex scroll-mt-24 flex-col rounded-lg border border-border bg-card', stacked ? 'p-6' : 'p-7')}>
      <h2 className="text-xl font-semibold">Free</h2>
      <p className={cn('mt-1 text-sm text-body', stacked && 'lg:min-h-10')}>For a few sites, checked every {facts.intervalSeconds} seconds.</p>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="num text-5xl font-semibold tracking-tight">$0</span>
        <span className="text-muted-foreground">/mo</span>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">No credit card needed</p>

      <Metrics items={metrics} stacked={stacked} />
      <FactList locationLine={<>One location: {facts.locationLabel}</>} bullets={bullets} />
      <p className="mt-4 text-sm text-muted-foreground">
        Not included: ping, port, DNS and SSL checks, browser checks, webhooks, API keys.
      </p>

      <div className="mt-7">{action}</div>
      {state === 'full' && (
        <p className="mt-3 text-center text-sm">
          <a href="#plan-pro" className="text-primary hover:underline">
            See Pro (from $3/mo)
          </a>
        </p>
      )}
    </div>
  )
}

export function GuaranteeStrip() {
  const items = [
    { title: '30-day money-back guarantee', text: 'On every paid plan. Not for you? Ask within 30 days for a full refund.', href: '/refund' as const },
    { title: 'Cancel in two clicks', text: 'Settings → Billing. You keep access to the end of the period.' },
    { title: 'Change plans anytime', text: 'Upgrades and downgrades are prorated.' },
  ]
  return (
    <div className="grid divide-y divide-border rounded-lg border border-border bg-card md:grid-cols-3 md:divide-x md:divide-y-0">
      {items.map((item) => (
        <div key={item.title} className="px-6 py-5">
          <h3 className="font-semibold text-foreground">{item.title}</h3>
          <p className="mt-1 text-sm text-body">
            {item.text}
            {item.href && (
              <>
                {' '}
                <Link to={item.href} className="text-primary hover:underline">
                  Refund policy
                </Link>
              </>
            )}
          </p>
        </div>
      ))}
    </div>
  )
}

/** Every check location, grouped by continent (src/lib/locations.ts). */
export function CheckLocations() {
  return (
    <MSection id="locations" tone="raised">
      <SectionHeader
        align="center"
        title="Where we check from"
        lead={MONITOR_LOCATIONS.plan.sectionLead}
      />
      <div className="mx-auto mt-10 grid max-w-5xl gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
        {CONTINENTS.map((continent) => (
          <div key={continent}>
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">{continent}</h3>
            <ul className="space-y-2">
              {LOCATIONS.filter((l) => l.continent === continent).map((l) => (
                <li key={l.code} className="flex items-center gap-2.5 text-[15px] text-body">
                  <span aria-hidden="true">{l.flag}</span>
                  <span>{l.city}</span>
                  {l.core && <span className="text-sm text-muted-foreground">· every check type</span>}
                  {!l.core && MONITOR_LOCATIONS.regionList.tag && (
                    <span className="text-sm text-muted-foreground">· {MONITOR_LOCATIONS.regionList.tag}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="mx-auto mt-10 max-w-2xl text-center text-sm text-body">
        {MONITOR_LOCATIONS.plan.sectionFooter}
      </p>
    </MSection>
  )
}

/** A comparison-table value: ticks and dashes for yes/no, the text otherwise. */
export function CompareCell({ value }: { value: string }) {
  if (value === 'Yes')
    return (
      <>
        <Check className="mx-auto h-4 w-4 text-primary" aria-hidden="true" />
        <span className="sr-only">Included</span>
      </>
    )
  if (value === '—')
    return (
      <>
        <Minus className="mx-auto h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Not included</span>
      </>
    )
  return <>{value}</>
}
